import { API_BASE_URL, MOTION_EXPORT_SESSIONS_URL } from './api';
import type { CustomPlacement, MotionResizeMode } from '../types';

export interface MotionExportSessionSettings {
  resizeMode: MotionResizeMode;
  warpMode?: 'fit' | 'custom';
  customPlacement?: CustomPlacement | null;
  diffuse: {
    hue: number;
    saturation: number;
    brightness: number;
    contrast: number;
    monochrome: boolean;
  };
  background: {
    transparent: boolean;
    mode: 'original' | 'tint' | 'tritone';
    shadowColor: string;
    midColor: string;
    highlightColor: string;
  };
  reflection: {
    enabled: boolean;
    opacity: number;
    blur: number;
  };
  useAlternateMask: boolean;
}

export interface MotionExportSessionCreatePayload {
  templateId: string;
  mediaName: string;
  mediaType: string;
  mediaKind: 'image' | 'video';
  settings: MotionExportSessionSettings;
}

export interface MotionExportSession {
  id: string;
  templateId: string;
  mediaName: string;
  mediaType: string;
  mediaKind: 'image' | 'video';
  settings: MotionExportSessionSettings;
  mediaUrl: string;
  createdAt: string;
  expiresAt: string;
}

export interface MotionExportSessionCreateResult {
  id: string;
  createdAt: string;
  expiresAt: string;
}

export async function createMotionExportSession(
  payload: MotionExportSessionCreatePayload,
  media: File
): Promise<MotionExportSessionCreateResult> {
  if (!MOTION_EXPORT_SESSIONS_URL) {
    throw new Error('Motion export session API URL is not configured.');
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  formData.append('media', media, media.name || payload.mediaName || 'motion-source');

  const response = await fetch(MOTION_EXPORT_SESSIONS_URL, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to create motion export session.'));
  }

  return (await response.json()) as MotionExportSessionCreateResult;
}

export async function loadMotionExportSession(sessionId: string): Promise<MotionExportSession> {
  if (!MOTION_EXPORT_SESSIONS_URL) {
    throw new Error('Motion export session API URL is not configured.');
  }

  const response = await fetch(`${MOTION_EXPORT_SESSIONS_URL}/${encodeURIComponent(sessionId)}`, {
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load motion export session.'));
  }

  const payload = (await response.json()) as MotionExportSession;
  return {
    ...payload,
    mediaUrl: resolveApiUrl(payload.mediaUrl)
  };
}

export function resolveApiUrl(value: string): string {
  if (!value) {
    return value;
  }

  try {
    return new URL(value, `${API_BASE_URL || window.location.origin}/`).toString();
  } catch {
    return value;
  }
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: string };
    return String(payload?.error || '').trim() || fallback;
  } catch {
    return fallback;
  }
}
